(() => {
  if (customElements.get('ep-product-gallery')) return;

  const STATES = Object.freeze({
    STATIC: 'STATIC',
    CAROUSEL_IDLE: 'CAROUSEL_IDLE',
    CAROUSEL_DRAGGING: 'CAROUSEL_DRAGGING',
    CAROUSEL_SETTLING: 'CAROUSEL_SETTLING',
    GALLERY_OPENING: 'GALLERY_OPENING',
    GALLERY_IDLE: 'GALLERY_IDLE',
    GALLERY_CLOSING: 'GALLERY_CLOSING',
    IMAGE_OPENING: 'IMAGE_OPENING',
    IMAGE_IDLE: 'IMAGE_IDLE',
    IMAGE_CLOSING: 'IMAGE_CLOSING',
  });

  const clamp = (value, min, max) => Math.min(max, Math.max(min, value));
  const nextFrame = () => new Promise((resolve) => requestAnimationFrame(() => requestAnimationFrame(resolve)));
  const padIndex = (value) => String(value + 1).padStart(2, '0');

  class EPProductGallery extends HTMLElement {
    connectedCallback() {
      if (this._connected) return;
      this._connected = true;
      this._abort = new AbortController();
      this._animations = new Set();
      this._position = 0;
      this._gesture = null;
      this._settle = null;
      this._imageOrigin = null;
      this._scrollLock = null;
      this._poorInteractions = 0;
      this._suppressClick = false;
      this._state = STATES.STATIC;

      this._cacheElements();
      if (!this._isValid()) return;

      this._readConfiguration();
      this._tier = this._chooseInitialTier();
      this.dataset.tier = this._tier;
      this._bindEvents();
      this._renderCarousel(0);
      this._handleBreakpoint();
      this._decodeSecondaryImages();
    }

    disconnectedCallback() {
      this._abort?.abort();
      this._cancelSettle(true);
      this._cancelStructuralAnimations(true);
      this._closeDialogsImmediately();
      this._unlockPage();
      this._mediaQuery?.removeEventListener?.('change', this._boundBreakpoint);
      this._connected = false;
    }

    _cacheElements() {
      this.host = this.closest('[data-ep-product-gallery-host]');
      this.stage = this.querySelector('[data-ep-stage]');
      this.cards = [...this.querySelectorAll('[data-ep-card]')];
      this.cardImages = this.cards.map((card) => card.querySelector('img'));
      this.previousButton = this.querySelector('[data-ep-previous]');
      this.nextButton = this.querySelector('[data-ep-next]');
      this.viewAllButton = this.querySelector('[data-ep-view-all]');
      this.counter = this.querySelector('[data-ep-current]');
      this.board = this.querySelector('[data-ep-board]');
      this.tiles = [...this.querySelectorAll('[data-ep-tile]')];
      this.tileImages = this.tiles.map((tile) => tile.querySelector('img'));
      this.closeBoardButton = this.querySelector('[data-ep-close-board]');
      this.viewer = this.querySelector('[data-ep-viewer]');
      this.viewerImage = this.querySelector('[data-ep-viewer-image]');
      this.closeViewerButton = this.querySelector('[data-ep-close-viewer]');
    }

    _isValid() {
      const count = Number(this.dataset.count);
      return Boolean(
        this.host &&
        this.stage &&
        this.board &&
        this.viewer &&
        this.viewerImage &&
        count > 0 &&
        this.cards.length === count &&
        this.tiles.length === count &&
        this.cardImages.every(Boolean) &&
        this.tileImages.every(Boolean)
      );
    }

    _readConfiguration() {
      this.count = Number(this.dataset.count);
      this.breakpoint = Number(this.dataset.breakpoint) || 750;
      this.dragDistance = Number(this.dataset.dragDistance) || 165;
      this.intentThreshold = Number(this.dataset.intentThreshold) || 8;
      this.swipeVelocity = Number(this.dataset.swipeVelocity) || 700;
      this.settleDuration = Number(this.dataset.settleDuration) || 420;
      this.layoutDuration = Number(this.dataset.layoutDuration) || 540;
      this.viewerDuration = Number(this.dataset.viewerDuration) || 460;
      this.easing = this.dataset.easing || 'cubic-bezier(0.2, 0.75, 0.25, 1)';
      this._mediaQuery = window.matchMedia(`(max-width: ${this.breakpoint}px)`);
      this._boundBreakpoint = () => this._handleBreakpoint();
      this._mediaQuery.addEventListener?.('change', this._boundBreakpoint);
    }

    _chooseInitialTier() {
      if (!('animate' in Element.prototype) || typeof this.board.showModal !== 'function') return 'C';
      if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return 'B';
      return 'A';
    }

    _bindEvents() {
      const { signal } = this._abort;

      this.previousButton?.addEventListener('click', () => this._step(-1), { signal });
      this.nextButton?.addEventListener('click', () => this._step(1), { signal });
      this.viewAllButton?.addEventListener('click', () => this._openBoard(), { signal });
      this.closeBoardButton?.addEventListener('click', () => this._closeBoard(), { signal });
      this.closeViewerButton?.addEventListener('click', () => this._closeViewer(), { signal });

      this.cards.forEach((card, index) => {
        card.addEventListener('click', (event) => this._onCardClick(event, index), { signal });
      });

      this.tiles.forEach((tile, index) => {
        tile.addEventListener('click', () => this._openViewer('gallery', index), { signal });
      });

      this.stage.addEventListener('pointerdown', (event) => this._onPointerDown(event), { signal });
      this.stage.addEventListener('pointermove', (event) => this._onPointerMove(event), { signal, passive: false });
      this.stage.addEventListener('pointerup', (event) => this._onPointerUp(event), { signal });
      this.stage.addEventListener('pointercancel', (event) => this._onPointerCancel(event), { signal });

      this.board.addEventListener('cancel', (event) => {
        event.preventDefault();
        if (this._state === STATES.GALLERY_IDLE) this._closeBoard();
      }, { signal });

      this.viewer.addEventListener('cancel', (event) => {
        event.preventDefault();
        if (this._state === STATES.IMAGE_IDLE) this._closeViewer();
      }, { signal });

      this.board.addEventListener('click', (event) => {
        if (event.target === this.board && this._state === STATES.GALLERY_IDLE) this._closeBoard();
      }, { signal });

      this.viewer.addEventListener('click', (event) => {
        if (event.target === this.viewer && this._state === STATES.IMAGE_IDLE) this._closeViewer();
      }, { signal });

      window.addEventListener('resize', () => this._onResize(), { signal, passive: true });
      window.addEventListener('orientationchange', () => this._onOrientationChange(), { signal, passive: true });

      const section = this.closest('.shopify-section');
      section?.addEventListener('shopify:product:select', (event) => this._onVariantSelect(event), { signal });
    }

    _handleBreakpoint() {
      const shouldEnhance = this._mediaQuery.matches;
      this.host.classList.toggle('is-ep-gallery-enhanced', shouldEnhance);
      this.toggleAttribute('data-ready', shouldEnhance);

      if (shouldEnhance) {
        if (this._state === STATES.STATIC) this._setState(STATES.CAROUSEL_IDLE);
        this._renderCarousel(this._position);
      } else {
        this._closeDialogsImmediately();
        this._unlockPage();
        this._setState(STATES.STATIC);
      }
    }

    _setState(nextState) {
      this._state = nextState;
      this.dataset.state = nextState;
    }

    _normalize(value) {
      return ((value % this.count) + this.count) % this.count;
    }

    _wrappedDelta(value) {
      let delta = ((value + this.count / 2) % this.count + this.count) % this.count - this.count / 2;
      if (this.count % 2 === 0 && delta === -this.count / 2) delta = this.count / 2;
      return delta;
    }

    _nearestPositionForIndex(index) {
      return this._position + this._wrappedDelta(index - this._normalize(this._position));
    }

    _geometryFor(index, position) {
      const radius = Math.min(window.innerWidth * (parseFloat(getComputedStyle(this).getPropertyValue('--ep-radius-x')) / 100), 430);
      const depthY = parseFloat(getComputedStyle(this).getPropertyValue('--ep-depth-y')) || 80;
      const rearScale = parseFloat(getComputedStyle(this).getPropertyValue('--ep-rear-scale')) || 0.54;
      const rearOpacity = parseFloat(getComputedStyle(this).getPropertyValue('--ep-rear-opacity')) || 0.18;
      const delta = this._wrappedDelta(index - position);

      if (this.count === 1) {
        return { delta: 0, depth: 1, x: 0, y: 8, z: 0, scale: 1, opacity: 1 };
      }

      if (this.count === 2) {
        const distance = Math.min(1, Math.abs(delta));
        const direction = delta === 0 ? 0 : Math.sign(delta);
        const depth = 1 - distance * 0.48;
        return {
          delta,
          depth,
          x: direction * radius * distance * 0.82,
          y: -depthY * (1 - depth),
          z: -220 * (1 - depth),
          scale: 1 - distance * (1 - Math.max(rearScale, 0.72)),
          opacity: 1 - distance * (1 - Math.max(rearOpacity, 0.52)),
        };
      }

      const angle = delta * ((Math.PI * 2) / this.count);
      const depth = (Math.cos(angle) + 1) / 2;
      return {
        delta,
        depth,
        x: Math.sin(angle) * radius,
        y: -depthY * (1 - depth) + depth * 8,
        z: -300 * (1 - depth),
        scale: rearScale + depth * (1 - rearScale),
        opacity: rearOpacity + depth * (1 - rearOpacity),
      };
    }

    _renderCarousel(position) {
      this._position = position;
      let nearestIndex = 0;
      let nearestDistance = Infinity;

      this.cards.forEach((card, index) => {
        const geometry = this._geometryFor(index, position);
        const distance = Math.abs(geometry.delta);
        if (distance < nearestDistance) {
          nearestDistance = distance;
          nearestIndex = index;
        }

        card.style.transform = `translate(-50%, -50%) translate3d(${geometry.x.toFixed(2)}px, ${geometry.y.toFixed(2)}px, ${geometry.z.toFixed(2)}px) scale(${geometry.scale.toFixed(4)})`;
        card.style.opacity = geometry.opacity.toFixed(4);
        card.style.zIndex = String(Math.round(geometry.depth * 100));
        card.style.pointerEvents = distance < 1.05 ? 'auto' : 'none';
        card.classList.toggle('is-front', distance < 0.5);
        card.tabIndex = distance < 0.5 ? 0 : -1;
        card.setAttribute('aria-hidden', distance < 1.05 ? 'false' : 'true');
      });

      this.activeIndex = nearestIndex;
      if (this.counter) this.counter.textContent = padIndex(nearestIndex);
    }

    _step(amount) {
      if (![STATES.CAROUSEL_IDLE, STATES.CAROUSEL_SETTLING].includes(this._state)) return;
      this._cancelSettle(true);
      this._animateCarouselTo(Math.round(this._position) + amount);
    }

    _animateCarouselTo(target) {
      if (this.count < 2) return;
      this._cancelSettle(true);

      const from = this._position;
      if (Math.abs(target - from) < 0.001) {
        this._renderCarousel(target);
        this._setState(STATES.CAROUSEL_IDLE);
        return;
      }

      this._setState(STATES.CAROUSEL_SETTLING);
      const duration = this._tier === 'C' ? 0 : this.settleDuration;
      if (!duration) {
        this._renderCarousel(target);
        this._setState(STATES.CAROUSEL_IDLE);
        return;
      }

      const animation = this.animate([{ opacity: 1 }, { opacity: 1 }], {
        duration,
        easing: this.easing,
        fill: 'both',
      });
      const probe = this._startFrameProbe();
      const settle = { animation, from, target, frame: 0 };
      this._settle = settle;

      const tick = () => {
        if (this._settle !== settle) return;
        const progress = animation.effect?.getComputedTiming().progress;
        if (typeof progress === 'number') this._renderCarousel(from + (target - from) * progress);
        if (animation.playState !== 'finished' && animation.playState !== 'idle') {
          settle.frame = requestAnimationFrame(tick);
        }
      };
      tick();

      animation.finished
        .then(() => {
          if (this._settle !== settle) return;
          cancelAnimationFrame(settle.frame);
          this._settle = null;
          probe();
          this._renderCarousel(target);
          this._setState(STATES.CAROUSEL_IDLE);
        })
        .catch(() => probe());
    }

    _cancelSettle(rebase) {
      const settle = this._settle;
      if (!settle) return;
      const progress = settle.animation.effect?.getComputedTiming().progress;
      if (rebase && typeof progress === 'number') {
        this._renderCarousel(settle.from + (settle.target - settle.from) * progress);
      }
      cancelAnimationFrame(settle.frame);
      settle.animation.cancel();
      this._settle = null;
    }

    _onPointerDown(event) {
      if (event.pointerType === 'mouse' && event.button !== 0) return;
      if (event.isPrimary === false) return;
      if (![STATES.CAROUSEL_IDLE, STATES.CAROUSEL_SETTLING].includes(this._state)) return;

      this._gesture = {
        pointerId: event.pointerId,
        startX: event.clientX,
        startY: event.clientY,
        lastX: event.clientX,
        lastTime: performance.now(),
        startPosition: this._position,
        intent: 'undecided',
      };
    }

    _onPointerMove(event) {
      const gesture = this._gesture;
      if (!gesture || gesture.pointerId !== event.pointerId) return;
      const dx = event.clientX - gesture.startX;
      const dy = event.clientY - gesture.startY;

      if (gesture.intent === 'undecided') {
        if (Math.hypot(dx, dy) < this.intentThreshold) return;
        if (Math.abs(dy) >= Math.abs(dx) * 1.08) {
          gesture.intent = 'vertical';
          return;
        }

        gesture.intent = 'horizontal';
        this._cancelSettle(true);
        gesture.startPosition = this._position;
        this._setState(STATES.CAROUSEL_DRAGGING);
        this._suppressClick = true;
        this.stage.setPointerCapture?.(event.pointerId);
      }

      if (gesture.intent !== 'horizontal') return;
      event.preventDefault();
      this._renderCarousel(gesture.startPosition - dx / this.dragDistance);
      gesture.lastX = event.clientX;
      gesture.lastTime = performance.now();
    }

    _onPointerUp(event) {
      const gesture = this._gesture;
      if (!gesture || gesture.pointerId !== event.pointerId) return;

      if (gesture.intent === 'horizontal') {
        const elapsed = Math.max(16, performance.now() - gesture.lastTime);
        const velocity = -((event.clientX - gesture.lastX) / elapsed) * 1000;
        let target = Math.round(this._position);

        if (Math.abs(velocity) >= this.swipeVelocity && target === Math.round(gesture.startPosition)) {
          target = Math.round(gesture.startPosition) + Math.sign(velocity);
        }

        this.stage.releasePointerCapture?.(event.pointerId);
        this._animateCarouselTo(target);
        window.setTimeout(() => {
          this._suppressClick = false;
        }, 0);
      }

      this._gesture = null;
    }

    _onPointerCancel(event) {
      const gesture = this._gesture;
      if (!gesture || gesture.pointerId !== event.pointerId) return;
      if (gesture.intent === 'horizontal') this._animateCarouselTo(Math.round(this._position));
      this._gesture = null;
      this._suppressClick = false;
    }

    _onCardClick(event, index) {
      if (this._suppressClick || this._state === STATES.CAROUSEL_DRAGGING) {
        event.preventDefault();
        return;
      }
      if (![STATES.CAROUSEL_IDLE, STATES.CAROUSEL_SETTLING].includes(this._state)) return;

      const targetPosition = this._nearestPositionForIndex(index);
      if (Math.abs(targetPosition - this._position) > 0.45) {
        this._animateCarouselTo(targetPosition);
        return;
      }

      this._cancelSettle(true);
      this._position = Math.round(targetPosition);
      this._renderCarousel(this._position);
      this._openViewer('carousel', index);
    }

    async _openBoard() {
      if (![STATES.CAROUSEL_IDLE, STATES.CAROUSEL_SETTLING].includes(this._state)) return;
      this._cancelSettle(true);
      this._setState(STATES.GALLERY_OPENING);
      this._lockPage();
      if (this._tier === 'A') {
        this.tileImages.forEach((image) => { image.dataset.epHiddenForFlight = 'true'; });
      }
      this.board.showModal();
      await this._decodeImages(this.tileImages);
      await nextFrame();

      if (this._tier === 'A') {
        await this._animatePairs(this.cardImages, this.tileImages, this.board, this.layoutDuration);
      } else {
        await this._fadeDialog(this.board, true, Math.min(220, this.layoutDuration));
      }

      this._setState(STATES.GALLERY_IDLE);
      this.closeBoardButton?.focus({ preventScroll: true });
    }

    async _closeBoard() {
      if (this._state !== STATES.GALLERY_IDLE) return;
      this._setState(STATES.GALLERY_CLOSING);
      this._renderCarousel(this._position);
      await nextFrame();

      if (this._tier === 'A') {
        await this._animatePairs(this.tileImages, this.cardImages, this.board, this.layoutDuration);
      } else {
        await this._fadeDialog(this.board, false, Math.min(200, this.layoutDuration));
      }

      this.board.close();
      this._setState(STATES.CAROUSEL_IDLE);
      this._unlockPage();
      this.viewAllButton?.focus({ preventScroll: true });
    }

    async _openViewer(originType, index) {
      const allowed = originType === 'gallery'
        ? this._state === STATES.GALLERY_IDLE
        : this._state === STATES.CAROUSEL_IDLE;
      if (!allowed) return;

      const originImage = originType === 'gallery' ? this.tileImages[index] : this.cardImages[index];
      const originButton = originType === 'gallery' ? this.tiles[index] : this.cards[index];
      if (!originImage || !originButton) return;

      this._setState(STATES.IMAGE_OPENING);
      if (originType === 'carousel') this._lockPage();
      this._imageOrigin = { type: originType, index, image: originImage, button: originButton };

      const sourceButton = originType === 'gallery' ? this.tiles[index] : this.cards[index];
      this.viewerImage.src = sourceButton.dataset.fullSrc || originImage.currentSrc || originImage.src;
      this.viewerImage.alt = originImage.alt || '';
      this.viewerImage.removeAttribute('srcset');
      if (this._tier === 'A') this.viewerImage.dataset.epHiddenForFlight = 'true';
      this.viewer.showModal();
      await this._decodeImages([this.viewerImage]);
      await nextFrame();

      if (this._tier === 'A') {
        await this._animatePairs([originImage], [this.viewerImage], this.viewer, this.viewerDuration);
      } else {
        await this._fadeDialog(this.viewer, true, Math.min(200, this.viewerDuration));
      }

      this._setState(STATES.IMAGE_IDLE);
      this.closeViewerButton?.focus({ preventScroll: true });
    }

    async _closeViewer() {
      if (this._state !== STATES.IMAGE_IDLE || !this._imageOrigin) return;
      this._setState(STATES.IMAGE_CLOSING);
      const origin = this._imageOrigin;
      const destination = origin.type === 'gallery' ? this.tileImages[origin.index] : this.cardImages[origin.index];
      await nextFrame();

      if (this._tier === 'A' && destination) {
        await this._animatePairs([this.viewerImage], [destination], this.viewer, this.viewerDuration);
      } else {
        await this._fadeDialog(this.viewer, false, Math.min(180, this.viewerDuration));
      }

      this.viewer.close();
      this.viewerImage.removeAttribute('src');
      this._imageOrigin = null;

      if (origin.type === 'gallery' && this.board.open) {
        this._setState(STATES.GALLERY_IDLE);
      } else {
        this._setState(STATES.CAROUSEL_IDLE);
        this._unlockPage();
      }
      origin.button?.focus({ preventScroll: true });
    }

    async _animatePairs(fromImages, toImages, container, duration) {
      this._cancelStructuralAnimations(true);
      const probe = this._startFrameProbe();
      const records = [];

      fromImages.forEach((fromImage, index) => {
        const toImage = toImages[index];
        if (!fromImage || !toImage) return;
        const first = fromImage.getBoundingClientRect();
        const last = toImage.getBoundingClientRect();
        if (!first.width || !first.height || !last.width || !last.height) return;

        const clone = fromImage.cloneNode(true);
        clone.removeAttribute('srcset');
        clone.className = 'ep-product-gallery__flight';
        Object.assign(clone.style, {
          left: `${first.left}px`,
          top: `${first.top}px`,
          width: `${first.width}px`,
          height: `${first.height}px`,
          transformOrigin: 'top left',
          zIndex: String(this._visualLayer(fromImage)),
        });
        container.appendChild(clone);
        fromImage.dataset.epHiddenForFlight = 'true';
        toImage.dataset.epHiddenForFlight = 'true';

        const translateX = last.left - first.left;
        const translateY = last.top - first.top;
        const scaleX = last.width / first.width;
        const scaleY = last.height / first.height;
        const firstOpacity = this._visualOpacity(fromImage);
        const lastOpacity = this._visualOpacity(toImage);
        const animation = clone.animate([
          { transform: 'translate3d(0, 0, 0) scale(1, 1)', opacity: firstOpacity },
          { transform: `translate3d(${translateX}px, ${translateY}px, 0) scale(${scaleX}, ${scaleY})`, opacity: lastOpacity },
        ], {
          duration,
          easing: this.easing,
          fill: 'both',
        });

        this._animations.add(animation);
        records.push({ animation, clone, fromImage, toImage });
      });

      if (!records.length) {
        this.querySelectorAll('[data-ep-hidden-for-flight]').forEach((element) => {
          delete element.dataset.epHiddenForFlight;
        });
        probe();
        return;
      }

      await Promise.allSettled(records.map(({ animation }) => animation.finished));
      records.forEach(({ animation, clone, fromImage, toImage }) => {
        this._animations.delete(animation);
        clone.remove();
        delete fromImage.dataset.epHiddenForFlight;
        delete toImage.dataset.epHiddenForFlight;
      });
      probe();
    }

    _visualOpacity(image) {
      const card = image.closest('[data-ep-card]');
      if (card) return Number.parseFloat(getComputedStyle(card).opacity) || 0;
      return Number.parseFloat(getComputedStyle(image).opacity) || 1;
    }

    _visualLayer(image) {
      const card = image.closest('[data-ep-card]');
      if (!card) return 20;
      return (Number.parseInt(getComputedStyle(card).zIndex, 10) || 0) + 20;
    }

    async _fadeDialog(dialog, opening, duration) {
      if (!duration || !('animate' in dialog)) return;
      const animation = dialog.animate(
        opening
          ? [{ opacity: 0, transform: 'scale(0.992)' }, { opacity: 1, transform: 'scale(1)' }]
          : [{ opacity: 1, transform: 'scale(1)' }, { opacity: 0, transform: 'scale(0.992)' }],
        { duration, easing: this.easing, fill: 'both' }
      );
      this._animations.add(animation);
      await animation.finished.catch(() => {});
      this._animations.delete(animation);
      animation.cancel();
    }

    _cancelStructuralAnimations(finish) {
      this._animations.forEach((animation) => {
        try {
          if (finish) animation.finish();
          else animation.cancel();
        } catch (_) {
          animation.cancel();
        }
      });
      this._animations.clear();
      this.querySelectorAll('.ep-product-gallery__flight').forEach((clone) => clone.remove());
      this.querySelectorAll('[data-ep-hidden-for-flight]').forEach((element) => {
        delete element.dataset.epHiddenForFlight;
      });
    }

    _startFrameProbe() {
      if (this._tier !== 'A') return () => {};
      let frame = 0;
      let previous = performance.now();
      let longFrames = 0;
      let active = true;

      const sample = (now) => {
        if (!active) return;
        if (now - previous > 38) longFrames += 1;
        previous = now;
        frame = requestAnimationFrame(sample);
      };
      frame = requestAnimationFrame(sample);

      return () => {
        if (!active) return;
        active = false;
        cancelAnimationFrame(frame);
        if (longFrames >= 3) this._poorInteractions += 1;
        else this._poorInteractions = Math.max(0, this._poorInteractions - 1);
        if (this._poorInteractions >= 2) {
          this._tier = 'B';
          this.dataset.tier = 'B';
        }
      };
    }

    _decodeImages(images) {
      return Promise.all(images.map((image) => {
        if (!image) return Promise.resolve();
        if (image.complete) return image.decode?.().catch(() => {}) || Promise.resolve();
        return image.decode?.().catch(() => {}) || Promise.resolve();
      }));
    }

    _decodeSecondaryImages() {
      const run = () => this._decodeImages([...this.cardImages.slice(1), ...this.tileImages]);
      if ('requestIdleCallback' in window) window.requestIdleCallback(run, { timeout: 1800 });
      else window.setTimeout(run, 600);
    }

    _lockPage() {
      if (this._scrollLock) return;
      const scrollY = window.scrollY;
      this._scrollLock = {
        scrollY,
        position: document.body.style.position,
        top: document.body.style.top,
        width: document.body.style.width,
        overflow: document.body.style.overflow,
      };
      document.body.style.position = 'fixed';
      document.body.style.top = `-${scrollY}px`;
      document.body.style.width = '100%';
      document.body.style.overflow = 'hidden';
    }

    _unlockPage() {
      if (!this._scrollLock) return;
      const lock = this._scrollLock;
      document.body.style.position = lock.position;
      document.body.style.top = lock.top;
      document.body.style.width = lock.width;
      document.body.style.overflow = lock.overflow;
      this._scrollLock = null;
      window.scrollTo(0, lock.scrollY);
    }

    _closeDialogsImmediately() {
      this._cancelStructuralAnimations(false);
      if (this.viewer?.open) this.viewer.close();
      if (this.board?.open) this.board.close();
      this.viewerImage?.removeAttribute('src');
      this._imageOrigin = null;
    }

    _onResize() {
      window.clearTimeout(this._resizeTimer);
      this._resizeTimer = window.setTimeout(() => {
        this._handleBreakpoint();
        if ([STATES.GALLERY_OPENING, STATES.GALLERY_CLOSING, STATES.IMAGE_OPENING, STATES.IMAGE_CLOSING].includes(this._state)) {
          this._cancelStructuralAnimations(true);
        }
        if (this._mediaQuery.matches) this._renderCarousel(this._position);
      }, 80);
    }

    _onOrientationChange() {
      this._cancelSettle(true);
      this._cancelStructuralAnimations(true);
      window.setTimeout(() => {
        if (this._mediaQuery.matches) this._renderCarousel(this._position);
      }, 180);
    }

    _onVariantSelect(event) {
      if (!(event.target instanceof Element) || event.target.closest('product-card')) return;
      const promise = event.promise;
      if (!promise || typeof promise.then !== 'function') return;

      promise.then(({ detail }) => {
        const html = detail?.html;
        if (!html) return;
        const replacement = html.querySelector(
          `ep-product-gallery[data-section-id="${CSS.escape(this.dataset.sectionId)}"]`
        );
        if (!replacement || replacement.dataset.productId !== this.dataset.productId) return;
        this.replaceWith(replacement);
      }).catch((error) => {
        if (error?.name !== 'AbortError') console.warn('[EP gallery] Variant media update failed.', error);
      });
    }
  }

  customElements.define('ep-product-gallery', EPProductGallery);
})();
